import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModuleAppShell from "../components/ModuleAppShell";
import RequirePagePermission from "../components/RequirePagePermission";
import { useAuth } from "../contexts/AuthContext";
import { useModuleScope } from "../contexts/ModuleScopeContext";
import { api, API_BASE, formatApiError } from "../lib/api";
import { ROLES } from "../lib/rbac";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Download, ChevronDown, AlertTriangle, Banknote, Wallet } from "lucide-react";
import { toast } from "sonner";
import DateInput from "../components/DateInput";
import HeroKPI from "../components/HeroKPI";
import StatusBadge from "../components/StatusBadge";
import EmptyState from "../components/EmptyState";
import useChartColors from "../hooks/useChartColors";
import {
    ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtUSDShort = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const lastMonthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() - 1, 1).toISOString().slice(0, 10); };
const lastMonthEnd = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 0).toISOString().slice(0, 10); };
const weekStart = () => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); return d.toISOString().slice(0, 10); };
const ytdStart = () => `${new Date().getFullYear()}-01-01`;

// Compute the prior window of the same length ending the day before `from`.
const priorWindow = (from, to) => {
    const f = new Date(from), t = new Date(to);
    const days = Math.max(1, Math.round((t - f) / 86400000) + 1);
    const pTo = new Date(f); pTo.setDate(pTo.getDate() - 1);
    const pFrom = new Date(pTo); pFrom.setDate(pFrom.getDate() - (days - 1));
    return [pFrom.toISOString().slice(0, 10), pTo.toISOString().slice(0, 10)];
};

// Iter59.p24 — cost-category colours are derived from useChartColors()
// inside <Body /> so they follow the active theme.

export default function ModuleFinance() {
    // Iter60.p48.1 — Gate Module Finance behind `finance.view_module`
    // so the Attendance Officer sees the friendly 403 banner instead
    // of a blank chart page when they land here via a shared link.
    return (
        <ModuleAppShell title="Module Finance">
            <RequirePagePermission perm="finance.view_module">
                <Body />
            </RequirePagePermission>
        </ModuleAppShell>
    );
}

function Body() {
    const scope = useModuleScope();
    const navigate = useNavigate();
    const { user } = useAuth();
    // Iter59.p24 — theme-aware chart palette.
    const chartColors = useChartColors();
    const COST_COLORS = {
        gross:      chartColors.series[0],
        overtime:   chartColors.series[2],
        uplift:     chartColors.series[4],
        allowances: chartColors.series[1],
        deductions: chartColors.series[3],
    };
    const [from, setFrom] = useState(monthStart());
    const [to, setTo] = useState(today());
    const [summary, setSummary] = useState(null);
    const [priorSummary, setPriorSummary] = useState(null);
    const [ledger, setLedger] = useState([]);
    const [fees, setFees] = useState([]);
    const [periods, setPeriods] = useState([]);
    const [ppeSummary, setPpeSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    const isReadOnly = user?.role === ROLES.ASL_VIEWER;

    useEffect(() => {
        if (!scope) return;
        setLoading(true);
        const q = `module_id=${scope.moduleId}&date_from=${from}&date_to=${to}`;
        const [pFrom, pTo] = priorWindow(from, to);
        const pq = `module_id=${scope.moduleId}&date_from=${pFrom}&date_to=${pTo}`;
        Promise.all([
            api.get(`/finance/summary?${q}`),
            api.get(`/finance/summary?${pq}`).catch(() => ({ data: null })),
            api.get(`/finance/ledger?${q}`),
            api.get(`/finance/management-fees?${q}`),
            api.get(`/payroll/periods?module_id=${scope.moduleId}&page_size=200`),
            api.get(`/ppe/summary?${q}`).catch(() => ({ data: null })),
        ])
            .then(([s, ps, l, f, p, pe]) => {
                setSummary(s.data);
                setPriorSummary(ps?.data || null);
                setLedger(l.data.items || []);
                setFees(f.data.items || []);
                setPeriods((p.data.items || []).filter((x) => x.start_date >= from && x.start_date <= to));
                setPpeSummary(pe?.data || null);
            })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [scope, from, to]);

    const mine = useMemo(
        () => (summary?.per_module || []).find((m) => m.module_id === scope?.moduleId) || {},
        [summary, scope],
    );
    const priorMine = useMemo(
        () => (priorSummary?.per_module || []).find((m) => m.module_id === scope?.moduleId) || {},
        [priorSummary, scope],
    );
    const fundingPipeline = summary?.funding_pipeline || {};
    const base = scope ? `/m/${scope.moduleCode.toLowerCase()}` : "/";

    // Cash released/paid — computed from period statuses that map to fund release / paid.
    const cashReleased = periods
        .filter((p) => ["paid", "closed"].includes(p.status))
        .reduce((acc, p) => acc + (p.totals?.funding_required_total || 0), 0);
    const cashPaid = cashReleased;

    // Sparkline of last 4 pay periods' cash-paid (chronological).
    const cashPaidSparkline = useMemo(() => {
        const sorted = [...periods].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
        return sorted.slice(-4).map((p) =>
            ["paid", "closed"].includes(p.status) ? (p.totals?.funding_required_total || 0) : 0
        );
    }, [periods]);

    // Cost breakdown pie data.
    const costBreakdown = useMemo(() => {
        const entries = [
            { key: "gross", label: "Gross (base)", value: (mine.gross_total || 0) - (mine.overtime_total || 0) - (mine.uplift_total || 0) - (mine.allowances_total || 0) },
            { key: "overtime", label: "Overtime", value: mine.overtime_total || 0 },
            { key: "uplift", label: "Uplift", value: mine.uplift_total || 0 },
            { key: "allowances", label: "Allowances", value: mine.allowances_total || 0 },
            { key: "deductions", label: "Deductions", value: mine.deductions_total || 0 },
        ].filter((e) => e.value > 0);
        return entries;
    }, [mine]);

    // Cash flow bar — per-period funding pipeline values.
    const cashFlowData = useMemo(() => {
        const sorted = [...periods].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
        return sorted.slice(-6).map((p) => ({
            name: p.name,
            requested: p.totals?.funding_required_total || 0,
            released: ["paid", "closed", "funded"].includes(p.status) ? (p.totals?.funding_required_total || 0) : 0,
            paid: ["paid", "closed"].includes(p.status) ? (p.totals?.funding_required_total || 0) : 0,
        }));
    }, [periods]);

    // Delta on Net Payable vs prior window.
    const netDelta = useMemo(() => {
        const cur = mine.net_payable_total || 0;
        const prev = priorMine.net_payable_total || 0;
        if (!priorSummary) return null;
        const abs = cur - prev;
        return {
            value: abs === 0 ? "0" : `${abs > 0 ? "+" : "−"}${fmtUSDShort(Math.abs(abs))}`,
            direction: abs > 0 ? "up" : (abs < 0 ? "down" : "flat"),
            label: "vs prior period",
        };
    }, [mine, priorMine, priorSummary]);

    if (!scope) return null;
    if (loading || !summary) return <FinanceSkeleton />;

    return (
        <section className="space-y-6" data-testid="module-finance-page">
            {/* Header + filters */}
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-label" style={{ color: "var(--asl-crimson-2)" }}>{scope.module.code}</div>
                    <h1 className="text-page-title mt-1">{scope.module.name} Finance</h1>
                </div>
                <div className="flex items-end gap-2 flex-wrap">
                    <Preset onClick={() => { setFrom(weekStart()); setTo(today()); }} label="This Week" />
                    <Preset onClick={() => { setFrom(monthStart()); setTo(today()); }} label="This Month" />
                    <Preset onClick={() => { setFrom(lastMonthStart()); setTo(lastMonthEnd()); }} label="Last Month" />
                    <Preset onClick={() => { setFrom(ytdStart()); setTo(today()); }} label="YTD" />
                    <div><div className="text-[10px] uppercase font-semibold" style={{ color: "var(--asl-text-3)" }}>From</div><DateInput value={from} className="w-40" onChange={setFrom} data-testid="mf-from" /></div>
                    <div><div className="text-[10px] uppercase font-semibold" style={{ color: "var(--asl-text-3)" }}>To</div><DateInput value={to} className="w-40" onChange={setTo} data-testid="mf-to" /></div>
                </div>
            </div>

            {/* Hero row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="mf-hero-kpis">
                <HeroKPI
                    label="Net Payable"
                    value={fmtUSD(mine.net_payable_total)}
                    delta={netDelta}
                    tone="accent"
                    onClick={() => navigate(`${base}/payroll`)}
                    testid="mf-hero-net"
                />
                <HeroKPI
                    label="Funding Required"
                    value={fmtUSD(mine.funding_required_total)}
                    sub={(fundingPipeline.requested || 0) > 0
                        ? `${fmtUSDShort(fundingPipeline.requested)} awaiting funds`
                        : "All funding requests met"}
                    onClick={() => navigate(`${base}/payroll/funding`)}
                    testid="mf-hero-funding"
                />
                <HeroKPI
                    label="Cash Paid"
                    value={fmtUSD(cashPaid)}
                    sparkline={cashPaidSparkline}
                    sub={`${cashPaidSparkline.filter((v) => v > 0).length} of last 4 periods`}
                    onClick={() => navigate(`${base}/payroll?status=closed`)}
                    testid="mf-hero-cash-paid"
                />
                <HeroKPI
                    label="Outstanding"
                    value={fmtUSD(mine.outstanding_amount)}
                    tone={(mine.outstanding_amount || 0) > 0 ? "danger" : "default"}
                    onClick={() => navigate(`${base}/payroll/funding?status=requested`)}
                    testid="mf-hero-outstanding"
                />
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <ChartCard title="Cost breakdown · this range" testid="mf-cost-breakdown">
                    {costBreakdown.length === 0 ? (
                        <EmptyState icon={Banknote} title="No cost in this range" description="Prepare payroll periods to see the breakdown." />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 items-center">
                            <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                    <Pie data={costBreakdown} dataKey="value" nameKey="label" innerRadius={55} outerRadius={90} paddingAngle={2}>
                                        {costBreakdown.map((e) => (
                                            <Cell key={e.key} fill={COST_COLORS[e.key]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(v) => fmtUSD(v)} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="space-y-2 pl-2" data-testid="mf-cost-breakdown-legend">
                                {costBreakdown.map((e) => (
                                    <div key={e.key} className="flex items-center justify-between text-xs">
                                        <div className="flex items-center gap-2">
                                            <span className="w-3 h-3 rounded-sm" style={{ background: COST_COLORS[e.key] }} />
                                            <span style={{ color: "var(--asl-text)" }}>{e.label}</span>
                                        </div>
                                        <span className="mono font-semibold" style={{ color: "var(--asl-text)" }}>{fmtUSD(e.value)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </ChartCard>

                <ChartCard title="Cash flow · last 6 periods" testid="mf-cash-flow">
                    {cashFlowData.length === 0 ? (
                        <EmptyState icon={Wallet} title="No period activity" description="Cash flow bars appear per payroll period." />
                    ) : (
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={cashFlowData} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                                <CartesianGrid vertical={false} stroke="var(--asl-divider)" strokeDasharray="3 3" />
                                <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
                                <YAxis tickFormatter={(v) => fmtUSDShort(v)} tickLine={false} axisLine={false} width={54} />
                                <Tooltip formatter={(v) => fmtUSD(v)} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Bar dataKey="requested" name="Requested" fill={chartColors.series[2]} radius={[4, 4, 0, 0]} />
                                <Bar dataKey="released" name="Released" fill={chartColors.series[4]} radius={[4, 4, 0, 0]} />
                                <Bar dataKey="paid" name="Paid" fill={chartColors.series[1]} radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>
            </div>

            {/* PPE & Clothing costs — preserved */}
            {ppeSummary && (
                <div className="asl-card p-5" data-testid="mf-ppe-section">
                    <div className="flex items-center justify-between mb-3">
                        <div className="asl-section-title">Clothing & PPE Costs</div>
                        <button onClick={() => navigate(`${base}/ppe`)} className="text-xs font-semibold" style={{ color: "var(--asl-navy-2)" }} data-testid="mf-ppe-open">
                            Open PPE →
                        </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4" data-testid="mf-ppe-kpis">
                        <MiniStat label="Total spend" value={fmtUSD(ppeSummary.totals?.total_spend)} accent onClick={() => navigate(`${base}/ppe`)} />
                        <MiniStat label="Items" value={ppeSummary.totals?.total_items || 0} onClick={() => navigate(`${base}/ppe`)} />
                        <MiniStat label="In circulation" value={ppeSummary.totals?.in_circulation || 0} onClick={() => navigate(`${base}/ppe?status=issued`)} />
                        <MiniStat label="Returned" value={ppeSummary.totals?.returned || 0} onClick={() => navigate(`${base}/ppe?status=returned`)} />
                        <MiniStat label="Replaced" value={ppeSummary.totals?.replaced || 0} onClick={() => navigate(`${base}/ppe?status=replaced`)} />
                        <MiniStat label="Lost/Damaged" value={(ppeSummary.totals?.lost || 0) + (ppeSummary.totals?.damaged || 0)} onClick={() => navigate(`${base}/ppe`)} />
                    </div>
                    {(ppeSummary.by_item_type || []).length > 0 && (
                        <table className="w-full text-xs" data-testid="mf-ppe-by-type">
                            <thead className="text-label" style={{ background: "var(--asl-bg-alt)" }}>
                                <tr><th className="text-left px-3 py-2">Item type</th><th className="text-right px-3 py-2">Count</th><th className="text-right px-3 py-2">Spend</th></tr>
                            </thead>
                            <tbody>
                                {ppeSummary.by_item_type.map((r) => (
                                    <tr key={r.item_type} className="border-t" style={{ borderColor: "var(--asl-divider)" }}>
                                        <td className="px-3 py-2">{(r.item_type || "").replace(/_/g, " ")}</td>
                                        <td className="px-3 py-2 mono text-right">{r.count}</td>
                                        <td className="px-3 py-2 mono text-right font-semibold" style={{ color: "var(--asl-navy-2)" }}>{fmtUSD(r.spend)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* Tabs — Periods / Ledger / Fees (preserved) */}
            <Tabs defaultValue="periods">
                <TabsList>
                    <TabsTrigger value="periods" data-testid="mf-tab-periods">Periods ({periods.length})</TabsTrigger>
                    <TabsTrigger value="ledger" data-testid="mf-tab-ledger">Ledger ({ledger.length})</TabsTrigger>
                    <TabsTrigger value="fees" data-testid="mf-tab-fees">Management Fee</TabsTrigger>
                </TabsList>
                <TabsContent value="periods">
                    <div className="asl-card overflow-hidden">
                        {periods.length === 0
                            ? <div className="p-6 text-sm" style={{ color: "var(--asl-text-2)" }} data-testid="mf-periods-empty">No payroll periods in this range.</div>
                            : <table className="asl-table asl-table--zebra" data-testid="mf-periods-table">
                                <thead className="text-label" style={{ background: "var(--asl-bg-alt)" }}>
                                    <tr><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Range</th>
                                        <th className="text-right px-3 py-2">Gross</th><th className="text-right px-3 py-2">Net</th>
                                        <th className="text-right px-3 py-2">Mgmt fee</th><th className="text-left px-3 py-2">Status</th></tr>
                                </thead>
                                <tbody>
                                    {periods.map((p) => {
                                        const t = p.totals || {};
                                        return (
                                            <tr key={p.id} className="border-t hover:bg-[color:var(--asl-bg-alt)] cursor-pointer"
                                                onClick={() => navigate(`${base}/payroll/${p.id}`)}
                                                style={{ borderColor: "var(--asl-divider)" }} data-testid={`mf-period-${p.id}`}>
                                                <td className="px-3 py-2 font-medium" style={{ color: "var(--asl-text)" }}>{p.name}</td>
                                                <td className="px-3 py-2 mono text-xs">{p.start_date} → {p.end_date}</td>
                                                <td className="px-3 py-2 mono text-xs text-right">{fmtUSD(t.gross_total)}</td>
                                                <td className="px-3 py-2 mono text-xs text-right font-semibold" style={{ color: "var(--asl-navy-2)" }}>{fmtUSD(t.net_payable_total)}</td>
                                                <td className="px-3 py-2 mono text-xs text-right">{fmtUSD(t.management_fee)}</td>
                                                <td className="px-3 py-2"><StatusBadge status={p.status} /></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>}
                    </div>
                </TabsContent>
                <TabsContent value="ledger">
                    <div className="asl-card overflow-hidden">
                        <div className="flex items-center justify-end gap-2 p-3 border-b" style={{ borderColor: "var(--asl-divider)" }}>
                            {!isReadOnly && (
                                <a href={`${API_BASE}/finance/ledger/export.csv?module_id=${scope.moduleId}&date_from=${from}&date_to=${to}`} target="_blank" rel="noreferrer">
                                    <Button size="sm" variant="outline" data-testid="mf-ledger-csv"><Download className="h-4 w-4 mr-1" /> CSV</Button>
                                </a>
                            )}
                        </div>
                        {ledger.length === 0
                            ? <div className="p-6 text-sm" style={{ color: "var(--asl-text-2)" }} data-testid="mf-ledger-empty">No ledger entries.</div>
                            : <table className="asl-table asl-table--zebra" data-testid="mf-ledger-table">
                                <thead className="text-label" style={{ background: "var(--asl-bg-alt)" }}>
                                    <tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Type</th>
                                        <th className="text-left px-3 py-2">Subject</th><th className="text-right px-3 py-2">Amount</th>
                                        <th className="text-left px-3 py-2">Status</th></tr>
                                </thead>
                                <tbody>
                                    {ledger.map((r, i) => (
                                        <tr key={i} className="border-t" style={{ borderColor: "var(--asl-divider)" }}>
                                            <td className="px-3 py-2 mono text-xs">{r.date}</td>
                                            <td className="px-3 py-2 text-xs">{r.type}</td>
                                            <td className="px-3 py-2 text-xs">{r.subject}</td>
                                            <td className="px-3 py-2 mono text-xs text-right">{fmtUSD(r.amount)}</td>
                                            <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>}
                    </div>
                </TabsContent>
                <TabsContent value="fees">
                    <div className="asl-card overflow-hidden">
                        {fees.length === 0
                            ? <div className="p-6 text-sm" style={{ color: "var(--asl-text-2)" }} data-testid="mf-fees-empty">No fees in range.</div>
                            : <table className="asl-table asl-table--zebra" data-testid="mf-fees-table">
                                <thead className="text-label" style={{ background: "var(--asl-bg-alt)" }}>
                                    <tr><th className="text-left px-3 py-2">Module</th><th className="text-right px-3 py-2">Periods</th>
                                        <th className="text-right px-3 py-2">Net Payable</th><th className="text-right px-3 py-2">Mgmt Fee</th></tr>
                                </thead>
                                <tbody>
                                    {fees.map((f) => (
                                        <tr key={f.module_id} className="border-t" style={{ borderColor: "var(--asl-divider)" }}>
                                            <td className="px-3 py-2 text-xs">{f.module_name}</td>
                                            <td className="px-3 py-2 mono text-xs text-right">{f.period_count}</td>
                                            <td className="px-3 py-2 mono text-xs text-right">{fmtUSD(f.net_payable_total)}</td>
                                            <td className="px-3 py-2 mono text-xs text-right font-semibold" style={{ color: "var(--asl-crimson-2)" }}>{fmtUSD(f.management_fee)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>}
                    </div>
                </TabsContent>
            </Tabs>

            {/* More metrics — all legacy KPIs collapsed */}
            <details className="asl-card p-0 overflow-hidden group" data-testid="mf-more-metrics">
                <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between hover:bg-[color:var(--asl-bg-alt)]">
                    <div className="asl-section-title">More metrics</div>
                    <ChevronDown className="h-4 w-4 opacity-60 transition-transform group-open:rotate-180" />
                </summary>
                <div className="p-5 pt-0 space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="mf-more-kpis">
                        <MiniStat label="Active workers" value={mine.worker_count || 0} onClick={() => navigate(`${base}/staff?status=active`)} />
                        <MiniStat label="Gross" value={fmtUSD(mine.gross_total)} onClick={() => navigate(`${base}/payroll`)} />
                        <MiniStat label="Overtime" value={fmtUSD(mine.overtime_total)} onClick={() => navigate(`${base}/payroll`)} />
                        <MiniStat label="Uplift" value={fmtUSD(mine.uplift_total)} onClick={() => navigate(`${base}/payroll`)} />
                        {/* Iter63 — Schedule bonus roll-up. Own KPI so it
                            never mixes with base / overtime buckets. */}
                        <MiniStat
                            label="Schedule bonuses"
                            value={fmtUSD(mine.schedule_bonus_total)}
                            onClick={() => navigate(`${base}/payroll`)}
                            testId="mf-mini-schedule-bonus"
                        />
                        <MiniStat label="Allowances" value={fmtUSD(mine.allowances_total)} onClick={() => navigate(`${base}/payroll`)} />
                        <MiniStat label="Deductions" value={fmtUSD(mine.deductions_total)} onClick={() => navigate(`${base}/payroll`)} />
                        <MiniStat label="Mgmt fee" value={fmtUSD(mine.management_fee)} onClick={() => navigate(`${base}/payroll`)} />
                        <MiniStat label="Cash released" value={fmtUSD(cashReleased)} onClick={() => navigate(`${base}/payroll?status=paid`)} />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="mf-more-pipeline">
                        {Object.entries(fundingPipeline).map(([k, v]) => (
                            <MiniStat
                                key={k}
                                label={k.replace(/_/g, " ")}
                                value={fmtUSD(v)}
                                onClick={() => navigate(`${base}/payroll/funding?status=${k}`)}
                                accent={k === "requested" && v > 0}
                                testid={`mf-more-pipeline-${k}`}
                            />
                        ))}
                    </div>
                </div>
            </details>
        </section>
    );
}

function ChartCard({ title, children, testid }) {
    return (
        <div className="asl-card p-5" data-testid={testid}>
            <div className="asl-section-title mb-3">{title}</div>
            {children}
        </div>
    );
}

function MiniStat({ label, value, accent, onClick, testid }) {
    return (
        <button
            onClick={onClick}
            className="asl-card p-3 text-left hover:shadow-md transition"
            data-testid={testid || `mf-mini-${String(label).toLowerCase().replace(/\s+/g, "-")}`}
        >
            <div className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: "var(--asl-text-3)" }}>{label}</div>
            <div className={`text-base font-bold mono mt-1`} style={{ color: accent ? "var(--asl-crimson-2)" : "var(--asl-text)" }}>{value}</div>
        </button>
    );
}

function Preset({ onClick, label }) {
    return <Button variant="outline" size="sm" onClick={onClick}>{label}</Button>;
}

function FinanceSkeleton() {
    return (
        <div className="space-y-6" data-testid="mf-skeleton">
            <div className="asl-shimmer h-8 w-64" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="asl-card p-5 space-y-3">
                        <div className="asl-shimmer h-3 w-24" />
                        <div className="asl-shimmer h-8 w-32" />
                        <div className="asl-shimmer h-3 w-20" />
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="asl-card p-5"><div className="asl-shimmer h-52 w-full rounded-lg" /></div>
                <div className="asl-card p-5"><div className="asl-shimmer h-52 w-full rounded-lg" /></div>
            </div>
        </div>
    );
}

// keep AlertTriangle imported to satisfy potential future use without lint churn
// eslint-disable-next-line no-unused-vars
const _ensureIconImport = AlertTriangle;
